import { parseNotAuthorized } from "@/lib/api/notAuthorized";
import { getBrowserPostHog } from "@/lib/posthog-browser";
import { GAIL_API } from "@/utils/common/constants/routes";
import { isTrialLimitResponse } from "@/utils/trialLimit";

/**
 * Browser PostHog event for permission refusals. Identity is on the event
 * itself because Europe is cookieless and never `identify`s.
 *
 * Toasts stay in `notAuthorized.ts` (marked bodies only, burst-deduped).
 * This capture is independent so Galactus AuthzFilter 403s still show up.
 */
export const AUTHZ_DENIED_EVENT = "authz_denied";

export type AuthzDeniedSource = "galactus" | "require_auth" | "route_guard";

export type AuthzDeniedIdentity = {
  orgId: string;
  auth0OrgId: string | null;
  auth0UserId: string;
  gailUserId: string;
};

export type AuthzDeniedCaptureInput = {
  source: AuthzDeniedSource;
  requestPath: string;
  httpStatus?: number;
  dataType?: string;
  level?: string;
};

export type ClassifiedAuthzDenial =
  | { source: "require_auth"; dataType?: string; level?: string }
  | { source: "galactus" };

let identity: AuthzDeniedIdentity | null = null;

export function setAuthzDeniedIdentity(next: AuthzDeniedIdentity | null): void {
  identity = next;
}

/** Test seam — identity is module state that would leak between cases. */
export function resetAuthzDeniedIdentityForTests(): void {
  identity = null;
}

export function getAuthzDeniedIdentity(): AuthzDeniedIdentity | null {
  return identity;
}

export function isGalactusRequestUrl(url: string, galactusBase = GAIL_API): boolean {
  if (!galactusBase) return false;
  const base = galactusBase.replace(/\/$/, "");
  return url === base || url.startsWith(`${base}/`);
}

export function requestPathFromUrl(url: string): string {
  try {
    const parsed =
      url.startsWith("http://") || url.startsWith("https://")
        ? new URL(url)
        : new URL(url, "https://placeholder.local");
    return parsed.pathname;
  } catch {
    return url.split("?")[0] ?? url;
  }
}

export function classifyAuthzDeniedResponse(
  status: number,
  responseText: string,
  requestUrl: string,
  galactusBase = GAIL_API
): ClassifiedAuthzDenial | null {
  if (status !== 403) return null;

  const marked = parseNotAuthorized(responseText);
  if (marked) {
    return { source: "require_auth", dataType: marked.dataType, level: marked.level };
  }

  let parsed: unknown = null;
  if (responseText) {
    try {
      parsed = JSON.parse(responseText) as unknown;
    } catch {
      parsed = null;
    }
  }
  if (isTrialLimitResponse(status, parsed)) return null;
  if (!isGalactusRequestUrl(requestUrl, galactusBase)) return null;
  return { source: "galactus" };
}

export function captureAuthzDenied(input: AuthzDeniedCaptureInput): void {
  if (typeof window === "undefined") return;

  try {
    const properties: Record<string, unknown> = {
      source: input.source,
      request_path: input.requestPath,
      org_id: identity?.orgId ?? null,
      auth0_org_id: identity?.auth0OrgId ?? null,
      auth0_user_id: identity?.auth0UserId ?? null,
      gail_user_id: identity?.gailUserId ?? null,
      page_pathname: window.location.pathname,
    };
    if (input.httpStatus !== undefined) properties.http_status = input.httpStatus;
    if (input.dataType) properties.data_type = input.dataType;
    if (input.level) properties.level = input.level;

    getBrowserPostHog()?.capture(AUTHZ_DENIED_EVENT, properties);
  } catch (e) {
    console.warn("[authz_denied] capture failed", e);
  }
}

export function reportAuthzDeniedResponse(
  status: number,
  responseText: string,
  requestUrl: string
): void {
  const classified = classifyAuthzDeniedResponse(status, responseText, requestUrl);
  if (!classified) return;

  captureAuthzDenied({
    source: classified.source,
    requestPath: requestPathFromUrl(requestUrl),
    httpStatus: status,
    dataType: classified.source === "require_auth" ? classified.dataType : undefined,
    level: classified.source === "require_auth" ? classified.level : undefined,
  });
}
